// Section Abonnement dynamique (Paramètres) : formule, statut, dates, montant,
// upgrade (montée de gamme uniquement), résiliation (double confirmation),
// réactivation et accès au Customer Portal Stripe.
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatErreur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Loader2, CreditCard, ArrowUpCircle, XCircle, RotateCcw } from "lucide-react";

const fmt = (n) => Math.round(n || 0).toLocaleString("fr-FR");
const dateFr = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR") : "—");
const LIBELLE_STATUT = {
  trial: "En essai", actif: "Actif", en_attente_paiement: "Paiement en attente",
  "résilié": "Résilié", "expiré": "Expiré",
};
const CONTACT = "contact@groupeaerhpolynesie.com / 87 73 78 15";

function Info({ label, valeur }) {
  return (
    <div className="rounded-lg bg-[#E8F4F4]/60 px-3 py-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-[#4A8A8A]">{label}</p>
      <p className="text-sm font-medium text-[#1D3D3D]">{valeur}</p>
    </div>
  );
}

export default function AbonnementSection() {
  const { abonnement, chargerAbonnement } = useAuth();
  const [config, setConfig] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const [upgradeOuvert, setUpgradeOuvert] = useState(false);
  const [cibleUpgrade, setCibleUpgrade] = useState(null);
  const [prorata, setProrata] = useState(null);
  const [confirmResiliation, setConfirmResiliation] = useState("");

  useEffect(() => {
    api.get("/abonnements/formules").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  if (!abonnement) {
    return <p data-testid="abonnement-vide" className="text-sm text-[#1D3D3D]/70">Aucun abonnement actif.</p>;
  }

  const formuleActuelle = config?.formules.find((f) => f.code === abonnement.formule);
  const rangActuel = formuleActuelle?.rang ?? 0;
  const formulesSuperieures = (config?.formules || []).filter((f) => f.rang > rangActuel);
  const montant = formuleActuelle ? formuleActuelle.prix[abonnement.periodicite] : null;
  const resilie = abonnement.statut === "résilié";

  const ouvrirUpgrade = async (f) => {
    setCibleUpgrade(f);
    setProrata(null);
    try {
      const { data } = await api.get("/abonnements/apercu-upgrade", { params: { formule: f.code, periodicite: abonnement.periodicite } });
      setProrata(data.montant_prorata);
    } catch { setProrata(null); }
  };

  const confirmerUpgrade = async () => {
    setEnCours(true);
    try {
      await api.post("/abonnements/upgrade", { formule: cibleUpgrade.code, periodicite: abonnement.periodicite });
      await chargerAbonnement();
      toast.success(`Vous êtes passé à la formule ${cibleUpgrade.libelle}.`);
      setUpgradeOuvert(false); setCibleUpgrade(null);
    } catch (e) { toast.error(formatErreur(e.response?.data?.detail)); }
    finally { setEnCours(false); }
  };

  const resilier = async () => {
    setEnCours(true);
    try {
      const { data } = await api.post("/abonnements/resilier");
      await chargerAbonnement();
      toast.success(`Abonnement résilié. Accès maintenu jusqu'au ${dateFr(data.date_fin)}.`);
      setConfirmResiliation("");
    } catch (e) { toast.error(formatErreur(e.response?.data?.detail)); }
    finally { setEnCours(false); }
  };

  const reactiver = async () => {
    setEnCours(true);
    try {
      await api.post("/abonnements/reactiver");
      await chargerAbonnement();
      toast.success("Abonnement réactivé.");
    } catch (e) { toast.error(formatErreur(e.response?.data?.detail)); }
    finally { setEnCours(false); }
  };

  const ouvrirPortail = async () => {
    setEnCours(true);
    try {
      const { data } = await api.post("/abonnements/portail", { origin: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(formatErreur(e.response?.data?.detail)); setEnCours(false); }
  };

  return (
    <div data-testid="abonnement-section">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Info label="Formule" valeur={formuleActuelle?.libelle || abonnement.formule} />
        <Info label="Périodicité" valeur={abonnement.periodicite === "mensuel" ? "Mensuel" : "Annuel"} />
        <Info label="Statut" valeur={<span data-testid="abonnement-statut">{LIBELLE_STATUT[abonnement.statut] || abonnement.statut}</span>} />
        <Info label={abonnement.statut === "trial" ? "Fin de l'essai" : "Prochain paiement"}
          valeur={<span data-testid="abonnement-date">{dateFr(abonnement.date_fin_trial && abonnement.statut === "trial" ? abonnement.date_fin_trial : abonnement.date_prochain_paiement)}</span>} />
        <Info label="Prochain prélèvement" valeur={montant != null ? `${fmt(montant)} XPF` : "—"} />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {/* Montée de gamme (upgrade) */}
        {formulesSuperieures.length > 0 && !resilie && (
          <Dialog open={upgradeOuvert} onOpenChange={setUpgradeOuvert}>
            <DialogTrigger asChild>
              <Button data-testid="bouton-changer-formule" className="bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white">
                <ArrowUpCircle className="h-4 w-4 mr-1" /> Changer de formule
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="dialog-upgrade">
              <DialogHeader>
                <DialogTitle>Monter en gamme</DialogTitle>
                <DialogDescription>Le changement est immédiat, avec un prorata calculé par Stripe.</DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                {formulesSuperieures.map((f) => (
                  <button key={f.code} data-testid={`upgrade-${f.code}`} onClick={() => ouvrirUpgrade(f)}
                    className={`w-full text-left rounded-lg border p-3 transition-colors ${cibleUpgrade?.code === f.code ? "border-[#1D3D3D] bg-[#E8F4F4]" : "border-[#4A8A8A]/30"}`}>
                    <div className="flex justify-between">
                      <span className="font-semibold text-[#1D3D3D]">{f.libelle}</span>
                      <span className="text-[#1D3D3D]">{fmt(f.prix[abonnement.periodicite])} XPF / {abonnement.periodicite === "mensuel" ? "mois" : "an"}</span>
                    </div>
                    <p className="text-xs text-[#1D3D3D]/60">{f.description}</p>
                  </button>
                ))}
                {cibleUpgrade && prorata != null && (
                  <p data-testid="upgrade-prorata" className="text-sm text-[#1D3D3D] bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    Montant facturé maintenant (prorata) : <strong>{fmt(prorata)} XPF</strong>
                  </p>
                )}
                <p className="text-xs text-[#1D3D3D]/60">
                  Pour passer à une formule inférieure, contactez AERH : {CONTACT}
                </p>
              </div>
              <DialogFooter>
                <Button variant="secondary" onClick={() => setUpgradeOuvert(false)} className="bg-[#E8F4F4] text-[#1D3D3D] border border-[#4A8A8A]/30">Annuler</Button>
                <Button data-testid="confirmer-upgrade" onClick={confirmerUpgrade} disabled={!cibleUpgrade || enCours}
                  className="bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white">
                  {enCours ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmer la montée de gamme"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Réactivation */}
        {resilie && (
          <Button data-testid="bouton-reactiver" onClick={reactiver} disabled={enCours}
            className="bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white">
            <RotateCcw className="h-4 w-4 mr-1" /> Réactiver mon abonnement
          </Button>
        )}

        {/* Customer Portal */}
        <Button data-testid="bouton-portail" variant="secondary" onClick={ouvrirPortail} disabled={enCours}
          className="bg-[#E8F4F4] text-[#1D3D3D] border border-[#4A8A8A]/30">
          <CreditCard className="h-4 w-4 mr-1" /> Gérer mon moyen de paiement
        </Button>

        {/* Résiliation */}
        {!resilie && (
          <Dialog onOpenChange={() => setConfirmResiliation("")}>
            <DialogTrigger asChild>
              <Button data-testid="bouton-resilier" variant="secondary" className="bg-red-50 text-red-700 border border-red-200 hover:bg-red-100">
                <XCircle className="h-4 w-4 mr-1" /> Résilier mon abonnement
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="dialog-resiliation">
              <DialogHeader>
                <DialogTitle>Résilier votre abonnement ?</DialogTitle>
                <DialogDescription>
                  Vous conserverez l'accès en lecture seule jusqu'au {dateFr(abonnement.date_prochain_paiement)}.
                  Vos données sont conservées pendant 12 mois après résiliation. Saisissez « RÉSILIER » pour confirmer.
                </DialogDescription>
              </DialogHeader>
              <Input data-testid="resiliation-confirme" value={confirmResiliation}
                onChange={(e) => setConfirmResiliation(e.target.value)} placeholder="RÉSILIER"
                className="bg-white border-[#4A8A8A]/30" />
              <DialogFooter>
                <Button data-testid="resiliation-valider" onClick={resilier}
                  disabled={confirmResiliation.trim().toUpperCase() !== "RÉSILIER" || enCours}
                  className="bg-red-600 hover:bg-red-700 text-white">
                  {enCours ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmer la résiliation"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
}
